/**
 * Defines the direction in which the carousel should scroll.
 * - `'left2right'`: Scrolls from left to right.
 * - `'right2left'`: Scrolls from right to left.
 */
type ScrollDirection = 'left2right' | 'right2left'; // Allowed scroll directions for the carousel


/**
 * Configuration object for initializing the Carousel.
 */
export type CarouselConfig = {
    /** CSS selector for the carousel container element. This is the outermost element that wraps the carousel track.*/
    selector: string; // Selector for the carousel container
    /** CSS selector for the inner track element that holds the carousel items.*/
    trackSelector: string; // Selector for the carousel track (scrollable area)
    /** Whether the carousel should start scrolling automatically. Defaults to true if not specified.*/
    autoStart?: boolean; // Enables autoplay if true
    /** Speed at which the carousel should scroll (pixels per frame). Defaults to 1 if not specified.*/
    speed?: number; // Scrolling speed in pixels per frame
    /** Whether the carousel should loop infinitely. If true, cloned items will be added to simulate endless scrolling.*/
    infinite?: boolean; // Enables infinite scroll if true
    /** Delay (in milliseconds) before autoplay resumes after user interaction. Defaults to 1000 ms. */
    pauseDelay?: number; // Delay before autoplay resumes after interaction
    /** Direction in which the carousel should scroll. Can be 'left2right' or 'right2left'. Defaults to 'right2left'. */
    direction?: ScrollDirection; // Direction of scroll
    /** Gap (in pixels) between carousel items.Defaults to 10 if not provided. */
    gap?: number; // Spacing between cards in the carousel
    /** Optional control selectors for next and previous buttons. */
    constrols?: { // Object containing selectors for control buttons
        /** CSS selector for the "next" button. */
        nextBtn: string; // Selector for next button
        /** CSS selector for the "previous" button. */
        prevBtn: string; // Selector for previous button
    }
};

/**
 * ## Carousel
 * Carousel is a scrollable, optionally infinite, draggable image or content slider.
 * 
 * ### Features:
 * - Autoplay with customizable speed and direction.
 * - Optional infinite looping with seamless cloning and recentering.
 * - Manual navigation via buttons or dragging (mouse/touch).
 * - Pause and resume functionality on interaction.
 * 
 * ### Usage:
 * Instantiate this class with a {@link CarouselConfig} object to enable the carousel behavior
 * on a specified DOM container and track element.
 * 
 * ### Example:
 * ```ts
 * const carousel = new Carousel({
 *   selector: '.carousel-container',
 *   trackSelector: '.carousel-track',
 *   autoStart: true,
 *   speed: 2,
 *   infinite: true,
 *   direction: 'right2left',
 *   constrols: {
 *     nextBtn: '.next',
 *     prevBtn: '.prev'
 *   }
 * });
 * ```
 */
export class Carousel {
    private readonly container: HTMLElement; // Reference to the carousel container element
    private readonly track: HTMLElement; // Reference to the scrollable track element inside the container
    private readonly originalCards: HTMLElement[]; // Original (non-cloned) carousel items/cards
    private readonly autoStart: boolean; // Whether the carousel should start autoplaying on init
    private readonly speed: number; // Speed of scrolling in pixels per frame
    private readonly infinite: boolean; // Whether the carousel should loop infinitely
    private readonly pauseDelay: number; // Delay in milliseconds before autoplay resumes after interaction
    private readonly direction: ScrollDirection; // Direction of scrolling: 'left2right' or 'right2left'
    private readonly gap: number; // Space in pixels between carousel cards
    private readonly cardWidth: number; // Width of a single card including the gap
    private isPaused: boolean; // Flag to indicate if autoplay is currently paused
    private scrollOffset: number; // Current horizontal scroll offset (in pixels)
    private scrollInterval: number | null; // ID of the interval used for autoplay scrolling
    private readonly controls: {
        nextBtn: string; // CSS selector for the "next" button
        prevBtn: string; // CSS selector for the "previous" button
    }
    private dragStartX: number; // X-coordinate where dragging started
    private dragging: boolean; // Flag to indicate if dragging is currently in progress

    constructor(config: CarouselConfig) {
        const container = document.querySelector(config.selector); // Selects the carousel container element from the DOM
        if (!(container instanceof HTMLElement)) throw new Error("Container not found or invalid"); // Validates the container element

        const track = container.querySelector(config.trackSelector); // Selects the track element inside the container
        if (!(track instanceof HTMLElement)) throw new Error("Track not found or invalid"); // Validates the track element

        this.container = container; // Assigns the container element to the class property
        this.track = track; // Assigns the track element to the class property
        this.originalCards = Array.from(this.track.children) as HTMLElement[]; // Collects all direct child elements (cards) of the track
        if (this.originalCards.length === 0) throw new Error("No cards found in track"); // Throws error if no cards are found

        this.autoStart = config.autoStart ?? true; // Sets autoplay flag, defaulting to true
        this.speed = config.speed ?? 1; // Sets scroll speed, defaulting to 1
        this.infinite = config.infinite ?? true; // Enables infinite scroll if not specified, defaults to true
        this.pauseDelay = config.pauseDelay ?? 1000; // Sets pause delay after interaction, defaulting to 1000ms
        this.direction = config.direction ?? 'right2left'; // Sets scroll direction, defaulting to 'right2left'
        this.gap = config.gap ?? 10; // Sets gap between cards, defaulting to 10px
        this.cardWidth = this.originalCards[0].offsetWidth + this.gap; // Calculates full card width including the gap

        this.isPaused = !this.autoStart; // Determines initial pause state based on autoStart
        this.scrollOffset = 0; // Initializes scroll offset to 0
        this.scrollInterval = null; // Initializes scroll interval as null (no autoplay running yet)

        this.controls = config.constrols ?? {
            nextBtn: '', // Fallback for next button selector if controls not provided
            prevBtn: '', // Fallback for previous button selector if controls not provided
        }

        this.dragStartX = 0; // Initializes drag start X position
        this.dragging = false; // Initializes dragging state as false

        this.init(); // Calls the initialization method to set up the carousel
    }

    /**
     * Initializes the carousel by setting up infinite scroll if enabled,
     * adding event listeners, enabling dragging functionality, and starting autoplay if configured.
     */
    private init(): void {
        if (this.infinite) this.cloneForInfiniteScroll(); // Clone cards to support infinite scrolling
        this.addEventListeners(); // Attach event listeners for controls and user interaction
        this.enableDragging(); // Enable mouse and touch dragging
        if (this.autoStart) this.play(); // Start autoplay if autoStart is true
    }

    /**
     * Clones the original carousel cards and appends/prepends them to the track
     * to simulate infinite scrolling in both directions.
     * It creates 3 sets of clones before and after the original set.
     * Updates the scroll offset and transforms the track to start in the middle.
     */
    private cloneForInfiniteScroll(): void {
        for (let i = 0; i < 3; i++) { // Repeat the cloning process 3 times
            this.originalCards.forEach(card => { // Loop through each original card
                const clone1 = card.cloneNode(true); // Clone the card (deep clone)
                const clone2 = card.cloneNode(true); // Clone again for the other end
                this.track.appendChild(clone1); // Append clone to the end of the track
                this.track.insertBefore(clone2, this.track.firstChild); // Prepend clone to the beginning of the track
            });
        }
        this.scrollOffset = this.cardWidth * this.originalCards.length * 3; // Calculate the middle offset based on number of prepended clones
        this.track.style.transform = `translateX(${-this.scrollOffset}px)`; // Move the track to start at the middle cloned position
    }

    /**
    * Starts the autoplay scrolling of the carousel.
    * Sets up a recurring interval that scrolls the track in the configured direction and speed.
    * Also triggers recentering logic to maintain infinite scroll illusion.
    */
    public play(): void {
        this.isPaused = false; // Resume autoplay by setting paused flag to false

        if (this.scrollInterval !== null) clearInterval(this.scrollInterval); // Clear any existing scroll interval

        this.scrollInterval = window.setInterval(() => { // Start a new interval for continuous scrolling
            if (!this.isPaused) { // Only scroll if not paused
                const dir = this.direction === 'left2right' ? -1 : 1; // Determine scroll direction multiplier
                this.scrollOffset += dir * this.speed; // Update scroll offset based on direction and speed
                this.track.style.transform = `translateX(${-this.scrollOffset}px)`; // Apply scroll via CSS transform
                this.recenterIfNeeded(); // Recenter if scroll has moved out of the middle zone
            }
        }, 16); // Run every 16ms (~60fps)
    }

    /**
     * Pauses the autoplay scrolling of the carousel.
     * Clears the scroll interval to stop automatic movement.
     */
    public pause(): void {
        this.isPaused = true; // Set paused flag to true to stop movement
        if (this.scrollInterval !== null) clearInterval(this.scrollInterval); // Clear the interval if it's running
    }

    /**
     * Temporarily pauses the autoplay and resumes it after a specified delay.
     * Useful for user interactions like clicking or dragging.
     */
    public pauseWithDelay(): void {
        this.pause(); // Immediately pause the autoplay
        setTimeout(() => this.play(), this.pauseDelay); // Resume autoplay after the configured delay
    }

    /**
     * Moves the carousel forward by one card width.
     * Useful for manual navigation via next button or similar triggers.
     */
    public next(): void {
        this.scrollOffset += this.cardWidth; // Increase scroll offset by one full card width
        this.track.style.transform = `translateX(${-this.scrollOffset}px)`; // Apply updated scroll position via transform
        this.recenterIfNeeded(); // Recenter if necessary to maintain infinite scroll illusion
    }

    /**
     * Moves the carousel backward by one card width.
     * Useful for manual navigation via previous button or similar triggers.
     */
    public prev(): void {
        this.scrollOffset -= this.cardWidth; // Decrease scroll offset by one full card width
        this.track.style.transform = `translateX(${-this.scrollOffset}px)`; // Apply updated scroll position via transform
        this.recenterIfNeeded(); // Recenter if necessary to maintain infinite scroll illusion
    }

    /**
     * Adds event listeners for carousel interaction.
     * - Pauses autoplay temporarily when the container is clicked.
     * - Binds click events to "next" and "previous" buttons if provided in config.
     */
    private addEventListeners(): void {
        this.container.addEventListener('click', () => this.pauseWithDelay()); // Pause autoplay briefly on container click

        if (this.controls.nextBtn && this.controls.prevBtn) { // Check if control button selectors are defined
            const nextBtn = document.querySelector(this.controls.nextBtn); // Select the "next" button from the DOM
            const prevBtn = document.querySelector(this.controls.prevBtn); // Select the "previous" button from the DOM

            nextBtn?.addEventListener('click', () => this.next()); // Add click handler to trigger next() if nextBtn exists
            prevBtn?.addEventListener('click', () => this.prev()); // Add click handler to trigger prev() if prevBtn exists
        }
    }

    /**
     * Enables mouse and touch dragging functionality for the carousel.
     * - On mouse drag: pauses autoplay, updates scroll position based on movement, and resumes after delay.
     * - On touch swipe: behaves similarly to mouse drag for mobile compatibility.
     */
    private enableDragging(): void {
        this.track.addEventListener('mousedown', (e: MouseEvent) => { // When mouse drag starts
            this.dragging = true; // Set dragging state to true
            this.dragStartX = e.pageX; // Record the initial X position of the mouse
            this.pause(); // Pause autoplay while dragging
            this.track.style.cursor = 'grabbing'; // Change cursor to indicate dragging
        });

        window.addEventListener('mouseup', () => { // When mouse drag ends
            if (this.dragging) { // Only act if dragging was in progress
                this.dragging = false; // Reset dragging state
                this.track.style.cursor = 'grab'; // Restore cursor style
                setTimeout(() => this.play(), this.pauseDelay); // Resume autoplay after delay
            }
        });

        this.track.addEventListener('mousemove', (e: MouseEvent) => { // While dragging with the mouse
            if (!this.dragging) return; // Do nothing if not dragging
            const dx = e.pageX - this.dragStartX; // Calculate horizontal distance moved
            this.dragStartX = e.pageX; // Update the starting point for the next move
            this.scrollOffset -= dx; // Update scroll offset based on drag distance
            this.track.style.transform = `translateX(${-this.scrollOffset}px)`; // Apply updated transform
            this.recenterIfNeeded(); // Recenter if scroll boundaries are exceeded
        });

        this.track.addEventListener('touchstart', (e: TouchEvent) => { // When touch drag starts
            this.dragStartX = e.touches[0].pageX; // Record the initial X position of the finger
            this.pause(); // Pause autoplay while dragging
        });

        this.track.addEventListener('touchmove', (e: TouchEvent) => { // While dragging with touch
            const dx = e.touches[0].pageX - this.dragStartX; // Calculate horizontal distance moved
            this.dragStartX = e.touches[0].pageX; // Update the starting point for the next move
            this.scrollOffset -= dx; // Update scroll offset based on swipe distance
            this.track.style.transform = `translateX(${-this.scrollOffset}px)`; // Apply updated transform
            this.recenterIfNeeded(); // Recenter if needed for infinite scroll
        });

        this.track.addEventListener('touchend', () => { // When touch drag ends
            setTimeout(() => this.play(), this.pauseDelay); // Resume autoplay after delay
        });
    }

    /**
     * Recenters the carousel scroll position if it moves too far left or right.
     * Ensures the illusion of infinite scrolling by resetting scroll offset to the midpoint.
     */
    private recenterIfNeeded(): void {
        const totalWidth = this.track.scrollWidth; // Total scrollable width of the track (including clones)
        const visibleWidth = this.container.offsetWidth; // Width of the visible container area
        const midPoint = (totalWidth / 2) - (visibleWidth / 2); // Midpoint offset to visually center the original content
        const range = this.cardWidth * this.originalCards.length; // Threshold range near the start/end before recentering

        if (this.scrollOffset <= range || this.scrollOffset >= totalWidth - range) { // Check if scroll offset is near edges
            this.scrollOffset = midPoint; // Reset offset to the midpoint
            this.track.style.transform = `translateX(${-this.scrollOffset}px)`; // Apply new position via transform
        }
    }

    /**
     * Cleans up the carousel instance by:
     * - Pausing autoplay
     * - Removing all event listeners by replacing the container node
     * - Restoring original (non-cloned) cards if infinite scroll was used
     * - Resetting styles applied to the track
     */
    public destroy(): void {
        this.pause(); // Stop the autoplay scrolling

        this.container.replaceWith(this.container.cloneNode(true)); // Replace the container with a clone to remove all event listeners

        if (this.infinite) { // If infinite scrolling was enabled
            while (this.track.firstChild) { // Remove all current children (including clones)
                this.track.removeChild(this.track.firstChild); // Remove one child at a time
            }

            this.originalCards.forEach(card => { // Re-add only the original cards
                this.track.appendChild(card); // Append original card back to the track
            });

            this.track.style.transform = ''; // Reset transform styling
        }

        this.track.style.cursor = ''; // Reset cursor style
    }


}

